import type { GameSummary } from '@badger-sentinel/protocol';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { ApiError, fetchGame } from '../api/games.js';
import { ImageSlot, preloadSlots, SLOT } from '../theme/imagery.js';
import { useBsTheme } from '../theme/useTheme.js';
import {
  ClassificationBar,
  CONSOLE_HEADER_H,
  inertUnless,
  SideCrestCluster,
  ThemeToggleButton,
} from './chrome.js';
import { EntryStages } from './EntryScreen.js';
import { SeatRoster } from './SeatSelectScreen.js';
import type { ClaimedSeat, JoinedSession } from './session.js';
import { sideOf } from './sides.js';

/**
 * The console shell — entry and seat select as **one** screen.
 *
 * They were two pages, and the seam showed: entry drew a copy of seat select's
 * header crest, faded out, and the router cut to the real one. Every complaint
 * about that beat came from the cut rather than from the timings — a duplicated
 * crest that lands a few pixels off reads as a jump, a form that unmounts
 * mid-fade rewinds itself on the way out, and a page that mounts fully composed
 * pops rather than arrives.
 *
 * So nothing is duplicated and nothing is routed. The classification bar and
 * the console header are mounted from the first frame; the header simply has no
 * height until there is a session, and the crest cluster inside it is the same
 * DOM node before and after. Entry dissolves in place, the header opens, the
 * crest settles into it, and the roster rises behind — one move, no seam.
 */

const EASE = 'cubic-bezier(.22,.61,.36,1)';

/**
 * `entry` → `handoff` → `roster`. `handoff` is the beat where the header opens
 * and entry dissolves; the roster comes up behind it rather than with it, so
 * the two motions read as consequence instead of coincidence.
 */
type Phase = 'entry' | 'handoff' | 'roster';

const HANDOFF_MS = 560;

/**
 * `tokens.css` clamps every transition under `prefers-reduced-motion`, and a
 * `setTimeout` is not a transition. The handoff's motion went away and its
 * 560 ms of dead time stayed, so `reduce` measured *slower* than the animated
 * path — the preference removed the thing worth watching and kept the wait.
 */
function reducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

export interface LobbyScreenProps {
  readonly baseUrl: string;
  /** A session restored from storage — mounts straight into the roster. */
  readonly session: JoinedSession | null;
  readonly onJoined: (session: JoinedSession) => void;
  readonly onClaimed: (claimed: ClaimedSeat) => void;
  readonly onExit: () => void;
  /** The session token expired or was rejected — back to entry, no way around it. */
  readonly onSessionExpired: () => void;
  /** Entry accepted `BSNT-DEMO`; the rest of the app now talks to the worker. */
  readonly onDemoActivated?: () => void;
}

export function LobbyScreen(props: LobbyScreenProps): JSX.Element {
  const { theme, toggle } = useBsTheme();
  const queryClient = useQueryClient();

  // Mirrored rather than read straight off the prop: the handoff has to start on
  // the click, and the prop only comes back a render later by way of App.
  const [joined, setJoined] = useState<JoinedSession | null>(props.session);
  const [phase, setPhase] = useState<Phase>(props.session ? 'roster' : 'entry');
  const [crestIn, setCrestIn] = useState(props.session !== null);
  const [tab, setTab] = useState<'seats' | 'scenario'>('seats');
  const [, forceTick] = useState(0);

  const queryKey = ['game', joined?.gameId, joined?.sessionToken];
  const game = useQuery<GameSummary, ApiError>({
    queryKey,
    queryFn: () => {
      if (!joined) throw new Error('unreachable: the roster query runs only once joined');
      return fetchGame(props.baseUrl, joined.gameId, joined.sessionToken);
    },
    enabled: joined !== null,
    // No push channel exists here (seat-select.md Open) — the seat token this
    // socket needs is what this page exists to obtain. Poll, and let a manual
    // refresh and the 409-triggered refetch carry the rest.
    refetchInterval: 5_000,
    retry: false,
  });

  useEffect(() => {
    if (game.isError && game.error instanceof ApiError && game.error.status === 401) {
      // `App` clears the stored session, but this component mirrors it — so
      // `joined` and `phase: 'roster'` survived, and the roster kept rendering
      // last-good data against a dead token. The only feedback was the age
      // ticker climbing and a REFRESH button that appeared to do nothing.
      setJoined(null);
      setPhase('entry');
      setCrestIn(false);
      setTab('seats');
      props.onSessionExpired();
    }
  }, [game.isError, game.error, props]);

  // A ticking "roster as of Ns ago" — seat-select.md: "show the roster's age".
  useEffect(() => {
    if (phase !== 'roster') return;
    const handle = setInterval(() => forceTick((n) => n + 1), 1_000);
    return () => clearInterval(handle);
  }, [phase]);

  // The handoff timeline. The crest starts a frame late on purpose: its node
  // mounts with this phase, and a node that has never painted its "before" state
  // jumps to the "after" one instead of transitioning to it.
  useEffect(() => {
    if (phase !== 'handoff') return;
    if (reducedMotion()) {
      setCrestIn(true);
      setPhase('roster');
      return;
    }
    const settle = setTimeout(() => setCrestIn(true), 60);
    const done = setTimeout(() => setPhase('roster'), HANDOFF_MS);
    return () => {
      clearTimeout(settle);
      clearTimeout(done);
    };
  }, [phase]);

  // The roster lands eight patches at once. Warming them during the handoff is
  // exactly what `preloadSlots` is for — the animated beat pays for the walk
  // instead of the player watching eight empty boxes fill in.
  useEffect(() => {
    if (!joined) return;
    const mine = joined.seats.filter((s) =>
      joined.role === 'GM' ? s.cell === 'GM' : s.team === joined.role,
    );
    preloadSlots(
      joined.scenario,
      mine.map((s) => SLOT.seatPatch(s.cell, joined.role)),
    );
  }, [joined]);

  function beginHandoff(session: JoinedSession): void {
    setJoined(session);
    props.onJoined(session);
    setPhase('handoff');
  }

  function exit(): void {
    setJoined(null);
    setPhase('entry');
    setCrestIn(false);
    setTab('seats');
    props.onExit();
  }

  async function refetchRoster(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey });
  }

  const rosterAgeS =
    phase === 'roster' && game.dataUpdatedAt
      ? Math.max(0, Math.round((Date.now() - game.dataUpdatedAt) / 1000))
      : null;

  const scenario = game.data?.scenario ?? joined?.scenario ?? '';
  const side = joined ? sideOf(joined.role, game.data?.sides ?? joined.sides) : null;
  const gamePhase = game.data?.phase;
  const onRoster = phase === 'roster';

  return (
    <div
      data-bs-theme={theme}
      style={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--bg)',
        color: 'var(--text)',
        overflow: 'hidden',
      }}
    >
      <ClassificationBar right={<ThemeToggleButton theme={theme} onToggle={toggle} />} />

      {/* The console header. Zero-height through entry, so the splash runs full
          bleed; it opens as part of the handoff and the crest arrives with it.
          `inert` because `height: 0; overflow: hidden` hides a button from the
          eye and not from the tab order: `← Exit` was focusable from the very
          first frame of the splash. */}
      <div
        {...inertUnless(phase !== 'entry')}
        style={{
          flex: 'none',
          height: phase === 'entry' ? 0 : CONSOLE_HEADER_H,
          background: 'var(--surface)',
          borderBottom: `1px solid ${phase === 'entry' ? 'transparent' : 'var(--border)'}`,
          display: 'flex',
          alignItems: 'center',
          padding: '0 20px',
          gap: 20,
          overflow: 'hidden',
          transition: `height .5s ${EASE},border-color .5s ${EASE}`,
        }}
      >
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 20,
            opacity: onRoster ? 1 : 0,
            transform: onRoster ? 'none' : 'translate3d(-10px,0,0)',
            transition: `opacity .4s ${EASE},transform .5s ${EASE}`,
            transitionDelay: onRoster ? '80ms' : '0ms',
          }}
        >
          <button
            type="button"
            onClick={exit}
            style={{
              flex: 'none',
              height: 34,
              padding: '0 11px',
              background: 'transparent',
              border: '1px solid var(--border-strong)',
              color: 'var(--text-2)',
              fontSize: 10,
              letterSpacing: '.14em',
              textTransform: 'uppercase',
            }}
          >
            ← Exit
          </button>
          <div
            style={{
              flex: 'none',
              fontFamily: 'var(--font-ui)',
              fontWeight: 700,
              fontSize: 15,
              letterSpacing: '.10em',
              textTransform: 'uppercase',
              color: 'var(--text-3)',
            }}
          >
            Badger Sentinel
          </div>
          {joined && (
            <>
              <Divider />
              <HeaderStat label="Session" value={joined.code} />
              <Divider />
              <HeaderStat label="Scenario" value={scenario || '—'} big />
              {gamePhase && gamePhase.status !== 'LOBBY' && (
                <>
                  <Divider />
                  <HeaderStat
                    label="Turn"
                    value={`${gamePhase.turn} / ${gamePhase.maxTurns} · ${gamePhase.phase}`}
                  />
                </>
              )}
            </>
          )}
        </div>

        {/* One crest, mounted once. Entry used to draw its own copy of this and
            hand over to a second one — that duplicate is what produced the cut. */}
        {joined && side && (
          <div
            style={{
              flex: 'none',
              transform: crestIn ? 'none' : 'translate3d(0,10px,0) scale(1.06)',
              opacity: crestIn ? 1 : 0,
              transition: `transform .52s ${EASE},opacity .4s ${EASE}`,
            }}
          >
            <SideCrestCluster
              name={side.name}
              identity={side.identity}
              accent={side.accent}
              crest={<ImageSlot slot={side.crestSlot} scenario={scenario} compact />}
            />
          </div>
        )}
      </div>

      <div style={{ flex: 1, minHeight: 0, position: 'relative', overflow: 'hidden' }}>
        {phase !== 'roster' && (
          <div
            {...inertUnless(phase === 'entry')}
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 2,
              opacity: phase === 'entry' ? 1 : 0,
              pointerEvents: phase === 'entry' ? 'auto' : 'none',
              transition: `opacity .5s ${EASE}`,
            }}
          >
            <EntryStages
              baseUrl={props.baseUrl}
              onJoined={beginHandoff}
              leaving={phase !== 'entry'}
              {...(props.onDemoActivated ? { onDemoActivated: props.onDemoActivated } : {})}
            />
          </div>
        )}

        {joined && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              opacity: onRoster ? 1 : 0,
              pointerEvents: onRoster ? 'auto' : 'none',
              transition: `opacity .4s ${EASE}`,
            }}
          >
            {onRoster && (
              <SeatRoster
                baseUrl={props.baseUrl}
                session={joined}
                game={game.data ?? null}
                rosterAgeS={rosterAgeS}
                isFetching={game.isFetching}
                onRefresh={refetchRoster}
                onClaimed={props.onClaimed}
                tab={tab}
                onTab={setTab}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Divider(): JSX.Element {
  return <div style={{ flex: 'none', width: 1, height: 34, background: 'var(--border)' }} />;
}

function HeaderStat(props: { label: string; value: string; big?: boolean }): JSX.Element {
  return (
    <div style={{ minWidth: 0 }}>
      <div
        style={{
          fontFamily: 'var(--font-data)',
          fontSize: 10,
          lineHeight: 1,
          letterSpacing: '.18em',
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
        }}
      >
        {props.label}
      </div>
      <div
        style={{
          fontFamily: props.big ? 'var(--font-ui)' : 'var(--font-data)',
          fontWeight: 700,
          fontSize: props.big ? 20 : 18,
          letterSpacing: '.02em',
          marginTop: 4,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          textTransform: props.big ? 'uppercase' : 'none',
        }}
      >
        {props.value}
      </div>
    </div>
  );
}
